/* ============
 * OrganizationSettings Transformer
 * ============
 *
 * The transformer for the organization settings (os).
 */

import Transformer from './transformer';
import atrans from './accountingProvider';

export default class OrganizationSettingsTransformer extends Transformer {
  /**
   * Transform fetched organization settings from API → frontend
   */
  static fetch(os) {
    return {
      billingNumberDefault: os.billing_number_default,
      smsFrom: os.sms_from,
      orderNumberDefault: os.order_number_default,
      customerNumberDefault: os.customer_number_default,
      invoiceNotes: os.invoice_notes,
      billNotes: os.bill_notes,
      creditNoteNotes: os.credit_note_notes,
      timeslotGap: os.timeslot_gap,
      remindersEnabled: os.reminders_enabled,
      defaultDueDays: os.default_due_days,
      messageTemplateTypeId: os.message_template_type_id,
      defaultEdiProductGroupName: os.default_edi_product_group_name,
      defaultEdiClientGroupName: os.default_edi_client_group_name,
      defaultEdiClientName: os.default_edi_client_name,
      defaultEdiClientContactPhoneNumber: os.default_edi_client_contact_phone_number,
      defaultEdiClientAddressStreetName: os.default_edi_client_address_street_name,
      defaultEdiClientAddressCityId: os.default_edi_client_address_city_id,
      defaultEdiClientAddressZipCode: os.default_edi_client_address_zipcode,
      defaultEdiTaxPercentage: os.default_edi_tax_percentage,
      ediOrderNumberDefault: os.edi_order_number_default,
      ediOrderDeliveryNoteNumberDefault: os.edi_order_delivery_note_number_default,
      invoiceEmailTitle: os.invoice_email_title,
      invoiceEmailTemplate: os.invoice_email_template,
      creditNoteEmailTitle: os.credit_note_email_title,
      creditNoteEmailTemplate: os.credit_note_email_template,
      reminderEmailTitle: os.reminder_email_title,
      reminderEmailTemplate: os.reminder_email_template,
      roundInvoiceTotal: os.round_invoice_total,
      defaultClientGroupId: os.default_client_group_id,
      tosAccepted: os.tos_accepted,
      marketingAllowed: os.marketing_allowed,
      customTos: os.custom_tos,
      supplierNumberDefault: os.supplier_number_default,
      productNumberDefault: os.product_number_default,
      productNumberInitial: os.product_number_initial,
      accountingProviderId: os.accounting_provider_id,
      accountingProvider:
        os.acounting_provider !== undefined ? atrans.fetch(os.acounting_provider) : undefined,
      productInfoDisplay: os.product_info_display,
      facebookPixelId: os.facebook_pixel_id,
      customOrderProductNote: os.order_product_note,
      ediBillingProcess: os.edi_billing_process,
      paymentIdentitificationGeneration: os.payment_identification_generation,
      paymentIdentitificationTemplate: os.payment_identification_template,
      paymentIdentitificationNumberLength: os.payment_identification_number_length,
      defaultProductTypeName: os.default_product_type_name,
      defaultShippingProvider: os.default_shipping_provider,
      defaultWarehouse: os.default_warehouse,
      priceFields: os.price_fields,
      visiblePriceFields: os.visible_price_fields,
      defaultPriceField: os.default_price_field,
      fikenCompanies: os.fiken_companies,
      hasFikenToken: os.fiken_has_access_token,

      hasPowerOfficeToken: os.poweroffice_has_access_token,
      powerofficeClientKey: os.poweroffice_client_key,
      powerofficeCompanySlug: os.poweroffice_company_slug,
      powerofficeAppKey: os.poweroffice_app_key,
      powerofficeIntegrationType: os.poweroffice_integration_type,

      unimicroAccessToken: os.unimicro_access_token,
      unimicroRefreshToken: os.unimicro_refresh_token,
      unimicroTokenExpiresAt: os.unimicro_token_expires_at,
      hasUniMicroToken: os.unimicro_has_access_token,
      uniMicroRefreshExpired: os.unimicro_refresh_expired,
      unimicroEnvironment: os.unimicro_environment,
      unimicroInvoiceSeriesId: os.unimicro_invoice_series_id,

      orderConfirmNotificationEmailTitle: os.order_confirm_notification_email_title,
      orderConfirmNotificationEmailTemplate: os.order_confirm_notification_email_template,
      orderTrackingEmailTitle: os.order_tracking_email_title,
      orderTrackingEmailTemplate: os.order_tracking_email_template,
      penaltyAfter: os.penalty_after,
      bulkPriceUpdateStatus: os.bulk_price_update_status,
      bulkPriceUpdateEntries: os.bulk_price_update_entries,
      receivablesAccountDefaultId:
        os.receivables_account_default_id != null ? os.receivables_account_default_id : null,
      incomeAccountDefaultId:
        os.income_account_default_id != null ? os.income_account_default_id : null,
      vatAccountDefaultId: os.vat_account_default_id != null ? os.vat_account_default_id : null,
      depositAccountDefaultId:
        os.deposit_account_default_id != null ? os.deposit_account_default_id : null,
    };
  }

  /**
   * Payload for PUT /organization_settings/accounting when only updating
   * chart-of-account ↔ org default links (does not touch accounting_provider_id).
   */
  static sendAccountingAccountDefaults(os) {
    return {
      receivables_account_default_id:
        os.receivablesAccountDefaultId != null ? os.receivablesAccountDefaultId : null,
      income_account_default_id:
        os.incomeAccountDefaultId != null ? os.incomeAccountDefaultId : null,
      vat_account_default_id: os.vatAccountDefaultId != null ? os.vatAccountDefaultId : null,
      deposit_account_default_id:
        os.depositAccountDefaultId != null ? os.depositAccountDefaultId : null,
    };
  }

  /**
   * Transform frontend organization settings → API send format
   */
  static send(os) {
    return {
      billing_number_default: os.billingNumberDefault,
      sms_from: os.smsFrom,
      order_number_default: os.orderNumberDefault,
      customer_number_default: os.customerNumberDefault,
      invoice_notes: os.invoiceNotes,
      bill_notes: os.billNotes,
      credit_note_notes: os.creditNoteNotes,
      timeslot_gap: os.timeslotGap,
      reminders_enabled: os.remindersEnabled,
      default_due_days: os.defaultDueDays,
      message_template_type_id: os.messageTemplateTypeId,
      default_edi_product_group_name: os.defaultEdiProductGroupName,
      default_edi_client_group_name: os.defaultEdiClientGroupName,
      default_edi_client_name: os.defaultEdiClientName,
      default_edi_client_contact_phone_number: os.defaultEdiClientContactPhoneNumber,
      default_edi_client_address_street_name: os.defaultEdiClientAddressStreetName,
      default_edi_client_address_city_id: os.defaultEdiClientAddressCityId,
      default_edi_client_address_zipcode: os.defaultEdiClientAddressZipCode,
      default_edi_tax_percentage: os.defaultEdiTaxPercentage,
      edi_order_number_default: os.ediOrderNumberDefault,
      edi_order_delivery_note_number_default: os.ediOrderDeliveryNoteNumberDefault,
      invoice_email_title: os.invoiceEmailTitle,
      invoice_email_template: os.invoiceEmailTemplate,
      credit_note_email_title: os.creditNoteEmailTitle,
      credit_note_email_template: os.creditNoteEmailTemplate,
      reminder_email_title: os.reminderEmailTitle,
      reminder_email_template: os.reminderEmailTemplate,
      round_invoice_total: os.roundInvoiceTotal,
      default_client_group_id: os.defaultClientGroupId,
      tos_accepted: os.tosAccepted,
      marketing_allowed: os.marketingAllowed,
      custom_tos: os.customTos,
      supplier_number_default: os.supplierNumberDefault,
      product_number_default: os.productNumberDefault,
      product_number_initial:
        os.productNumberInitial !== undefined ? os.productNumberInitial.toUpperCase() : '',
      accounting_provider_id: os.accountingProviderId,
      product_info_display: os.productInfoDisplay,
      facebook_pixel_id: os.facebookPixelId,
      custom_order_product_note: os.customOrderProductNote,
      edi_billing_process: os.ediBillingProcess,
      payment_identification_generation: os.paymentIdentitificationGeneration,
      payment_identification_template: os.paymentIdentitificationTemplate,
      payment_identification_number_length: os.paymentIdentitificationNumberLength,
      default_product_type_name: os.defaultProductTypeName,
      default_shipping_provider: os.defaultShippingProvider,
      default_warehouse: os.defaultWarehouse,
      price_fields: os.priceFields,
      visible_price_fields: os.visiblePriceFields,
      default_price_field: os.defaultPriceField,

      unimicroClientId: os.unimicro_client_id,
      unimicroClientSecret: os.unimicro_client_secret,
      unimicroAccessToken: os.unimicro_access_token,
      unimicroRefreshToken: os.unimicro_refresh_token,
      unimicroTokenExpiresAt: os.unimicro_token_expires_at,
      hasUniMicroToken: os.unimicro_has_access_token,
      uniMicroRefreshExpired: os.unimicro_refresh_expired,
      unimicroEnvironment: os.unimicro_environment,

      order_confirm_notification_email_title: os.orderConfirmNotificationEmailTitle,
      order_confirm_notification_email_template: os.orderConfirmNotificationEmailTemplate,
      order_tracking_email_title: os.orderTrackingEmailTitle,
      order_tracking_email_template: os.orderTrackingEmailTemplate,
      penalty_after: os.penaltyAfter,
      receivables_account_default_id: os.receivablesAccountDefaultId,
      income_account_default_id: os.incomeAccountDefaultId,
      vat_account_default_id: os.vatAccountDefaultId,
      deposit_account_default_id: os.depositAccountDefaultId,
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/transformers/organizationSettings.js